import { useState } from 'react';
import type { AdvisorSettings, AIPlaystyle, LLMConfig } from '../lib/aiAdvisor';
import { sounds } from '../lib/soundEffects';

interface Props {
  settings: AdvisorSettings;
  onSave: (settings: AdvisorSettings) => void;
  onClose: () => void;
}

export function AISettingsModal({ settings, onSave, onClose }: Props) {
  const [playstyle, setPlaystyle] = useState<AIPlaystyle>(settings.playstyle);
  const [priceEnforcing, setPriceEnforcing] = useState<boolean>(settings.priceEnforcing);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(!sounds.isMuted());
  const [provider, setProvider] = useState<LLMConfig['provider']>(settings.llmConfig?.provider ?? 'builtin');
  const [apiKey, setApiKey] = useState<string>(settings.llmConfig?.apiKey ?? '');
  const [model, setModel] = useState<string>(settings.llmConfig?.model ?? '');

  const handleSave = () => {
    sounds.setMuted(!soundEnabled);
    onSave({
      ...settings,
      playstyle,
      priceEnforcing,
      llmConfig: {
        provider,
        apiKey: apiKey.trim(),
        model: model.trim() || undefined,
      },
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <div className="bg-slate-900 border-2 border-cyan-500/80 rounded-2xl w-full max-w-lg shadow-2xl shadow-cyan-950/50 overflow-hidden">
        {/* Header */}
        <div className="p-4 px-6 bg-slate-800/80 border-b border-cyan-500/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">⚙️</span>
            <div>
              <h3 className="text-base font-extrabold text-white">Rob's AI Advisor Settings</h3>
              <p className="text-xs text-slate-400">Fine-tune your bidding algorithms & LLM integrations</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 px-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 text-xs text-slate-200 max-h-[75vh] overflow-y-auto">
          {/* Section 1: Playstyle */}
          <div>
            <label className="font-bold text-white text-sm block mb-1.5">
              Draft Playstyle & Bidding Aggression
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['conservative', 'balanced', 'aggressive'] as const).map(style => (
                <button
                  key={style}
                  type="button"
                  onClick={() => setPlaystyle(style)}
                  className={`p-2.5 rounded-xl border text-center transition ${
                    playstyle === style
                      ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300 font-extrabold shadow'
                      : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <div className="capitalize text-xs font-bold">{style}</div>
                  <div className="text-[10px] opacity-75 mt-0.5">
                    {style === 'conservative' ? 'Bargain hunter' : style === 'balanced' ? 'Optimal ROI' : 'Win-now bully'}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Section 2: Tactical Enforcer */}
          <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-bold text-white block">Price Enforcing (Trap Bidding)</span>
                <span className="text-[11px] text-slate-400">
                  Allow AI to recommend bidding up rivals if they are getting marquee teams for too cheap.
                </span>
              </div>
              <input
                type="checkbox"
                checked={priceEnforcing}
                onChange={e => setPriceEnforcing(e.target.checked)}
                className="w-5 h-5 accent-cyan-500 rounded cursor-pointer"
              />
            </div>
          </div>

          {/* Section 3: Sound cues */}
          <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-bold text-white block">Draft Sound Cues</span>
                <span className="text-[11px] text-slate-400">
                  Subtle audio alerts for shot clock ticks, new high bids, and your turn.
                </span>
              </div>
              <input
                type="checkbox"
                checked={soundEnabled}
                onChange={e => setSoundEnabled(e.target.checked)}
                className="w-5 h-5 accent-cyan-500 rounded cursor-pointer"
              />
            </div>
          </div>

          {/* Section 4: LLM Engine Configuration */}
          <div className="border-t border-slate-800 pt-4 space-y-3">
            <div>
              <label className="font-bold text-white text-sm block mb-1">
                LLM Engine / Provider
              </label>
              <p className="text-[11px] text-slate-400 mb-2">
                The built-in model generates instant, zero-latency mathematical game theory. Optionally plug in an API key for dynamic external LLM analysis.
              </p>
              <select
                value={provider}
                onChange={e => setProvider(e.target.value as LLMConfig['provider'])}
                className="w-full p-2 bg-slate-950 border border-slate-700 rounded-lg text-white font-medium"
              >
                <option value="builtin">Built-in Strategic AI Engine (Fast, No Key Needed)</option>
                <option value="openai">OpenAI (GPT-4o / GPT-4o-mini)</option>
                <option value="openrouter">OpenRouter (Claude 3.5 / Llama 3 / etc.)</option>
              </select>
            </div>

            {provider !== 'builtin' && (
              <div className="space-y-3 bg-slate-950/60 p-3 rounded-xl border border-slate-800">
                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">
                    {provider.toUpperCase()} API Key
                  </label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-xs font-mono"
                  />
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    Stored 100% locally in your browser. Never sent anywhere else.
                  </span>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-slate-300 block mb-1">
                    Custom Model (Optional)
                  </label>
                  <input
                    type="text"
                    value={model}
                    onChange={e => setModel(e.target.value)}
                    placeholder={provider === 'openai' ? 'gpt-4o-mini' : 'anthropic/claude-3.5-haiku'}
                    className="w-full p-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-xs font-mono"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-800/80 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-xs">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs shadow-lg shadow-cyan-500/20"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
